<template>
  <div
    class="story-card"
    :class="{
      'is-blocked': isInteractionBlocked,
      'is-compact': props.compact,
      'has-audit-notice': isAuditRejected || isAuditPending,
      'has-audit-pending': isAuditPending
    }"
  >
    <!-- 封面 -->
    <button
      class="card-cover"
      :class="{ 'is-generating-cover': isInteractionBlocked }"
      type="button"
      :disabled="isInteractionBlocked"
      @click="handleCardClick"
    >
      <SvgIcon
        v-if="isInteractionBlocked"
        class="generating-cover-placeholder"
        name="story-cover-generating"
      />
      <img v-else-if="coverImageSrc" :src="coverImageSrc" alt="" class="cover-image" />
      <SvgIcon v-else name="image" :size="32" color="#d1d5db" />
    </button>

    <!-- 信息区 -->
    <div class="card-info">
      <h3
        class="card-title"
        :class="{
          'has-audit-rejection': isAuditRejected,
          'has-audit-pending': isAuditPending
        }"
      >
        <button
          class="card-title-button"
          type="button"
          :disabled="isInteractionBlocked"
          @click="openTitleEditDialog"
        >
          {{ story.title }}
        </button>
        <template v-if="isAuditRejected">
          <button
            v-if="props.compact"
            class="audit-rejection-badge"
            type="button"
            @click.stop="isAuditPopoverVisible = true"
          >
            <span class="audit-rejection-badge__message">绘本未通过审核，</span>
            <span class="audit-rejection-badge__action">查看详情</span>
          </button>
          <el-popover
            v-else
            v-model:visible="isAuditPopoverVisible"
            placement="right-start"
            :width="480"
            :offset="15"
            :show-arrow="false"
            trigger="click"
            popper-class="story-audit-rejection-popover"
          >
            <template #reference>
              <button class="audit-rejection-badge" type="button" @click.stop>
                <span class="audit-rejection-badge__message">绘本未通过审核，</span>
                <span class="audit-rejection-badge__action">查看详情</span>
              </button>
            </template>

            <section class="audit-rejection-dialog" role="dialog" aria-label="审核未通过提示">
              <header class="audit-rejection-dialog__header">
                <h4 class="audit-rejection-dialog__title">审核未通过提示</h4>
                <button
                  class="audit-rejection-dialog__close"
                  type="button"
                  aria-label="关闭审核未通过提示"
                  @click="closeAuditDialog"
                >
                  <SvgIcon name="modal-close" :size="12" />
                </button>
              </header>
              <div class="audit-rejection-dialog__body">
                <div>
                  很抱歉，您的绘本审核未通过，原因是
                  <span class="audit-rejection-dialog__opinion" v-html="auditOpinionHtml"></span>
                </div>
                <div class="audit-customer-service-row">
                  <span>详情咨询客服：</span>
                  <el-popover
                    v-model:visible="isCustomerServiceVisible"
                    placement="top-start"
                    :width="188"
                    :offset="8"
                    :show-arrow="true"
                    :persistent="false"
                    trigger="click"
                    popper-class="story-customer-service-popover"
                  >
                    <template #reference>
                      <button class="audit-customer-service__trigger" type="button" @click.stop>
                        点击添加
                      </button>
                    </template>

                    <section class="audit-customer-service" aria-label="微信扫码添加客服">
                      <header class="audit-customer-service__header">
                        <strong class="audit-customer-service__title">微信扫码添加客服</strong>
                        <button
                          class="audit-customer-service__close"
                          type="button"
                          aria-label="关闭客服二维码"
                          @click.stop="isCustomerServiceVisible = false"
                        >
                          <SvgIcon name="modal-close" :size="12" />
                        </button>
                      </header>
                      <img
                        class="audit-customer-service__qr"
                        :src="customerServiceQrUrl"
                        alt="添加客服二维码"
                      />
                      <span class="audit-customer-service__hours">工作日 9:00 – 18:00</span>
                    </section>
                  </el-popover>
                </div>
              </div>
              <footer class="audit-rejection-dialog__footer">
                <button
                  class="audit-rejection-dialog__confirm"
                  type="button"
                  @click="closeAuditDialog"
                >
                  我知道了
                </button>
              </footer>
            </section>
          </el-popover>
        </template>
        <span v-else-if="isAuditPending && !props.compact" class="audit-pending-badge">
          <span>分享等待审核中，不影响阅读。</span>
          <button class="audit-pending-badge__upgrade" type="button" @click.stop="emit('upgrade')">
            升级会员
          </button>
          <span class="audit-pending-badge__suffix">可享加速审核，即时发布</span>
        </span>
        <button
          class="card-title-edit-button"
          type="button"
          :disabled="isInteractionBlocked"
          aria-label="修改故事名称"
          title="修改名称"
          @click.stop="openTitleEditDialog"
        >
          <SvgIcon name="story-title-edit" :size="16" />
        </button>
      </h3>
      <div class="card-meta">
        <span class="meta-item">
          <SvgIcon name="calendar" :size="14" />
          <span class="desktop-date">创建 {{ formatDate(story.createdAt) }}</span>
          <span class="mobile-date">创建 {{ formatDateOnly(story.createdAt) }}</span>
        </span>
        <span v-if="!isInteractionBlocked" class="meta-item">
          <SvgIcon name="refresh" :size="14" />
          <span>更新 {{ formatDate(story.updatedAt) }}</span>
        </span>
      </div>
    </div>

    <span
      v-if="props.compact && isAuditPending"
      class="audit-pending-badge audit-pending-badge--compact"
    >
      <span>分享等待审核中，不影响阅读。</span>
      <button class="audit-pending-badge__upgrade" type="button" @click.stop="emit('upgrade')">
        升级会员
      </button>
      <span class="audit-pending-badge__suffix">可享加速审核，即时发布</span>
    </span>

    <div v-if="isGenerating" class="generation-status" aria-live="polite">
      <svg
        class="generation-loading-icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clip-path="url(#story_loading_gradient_clip_path)" data-figma-skip-parse="true">
          <g transform="matrix(0.0119612 1.42407e-06 0 -0.0119986 11.9612 12)">
            <foreignObject x="-1083.6" y="-1083.6" width="2167.21" height="2167.21">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style="
                  background: conic-gradient(
                    from 90deg,
                    rgba(99, 36, 235, 1) 0deg,
                    rgba(241, 82, 158, 1) 136.8deg,
                    rgba(255, 255, 255, 0) 360deg
                  );
                  height: 100%;
                  width: 100%;
                  opacity: 1;
                "
              ></div>
            </foreignObject>
          </g>
        </g>
        <path
          d="M11.9612 23.9986C18.5672 23.9994 23.9224 18.6281 23.9224 12.0014C23.9224 5.37479 18.5672 0.00221056 11.9612 0.00142407C5.35522 0.000637576 0 5.37195 0 11.9986C0 18.6252 5.35522 23.9978 11.9612 23.9986ZM11.9612 3.98878C16.3719 3.9893 19.9475 7.57647 19.9475 12.001C19.9475 16.4254 16.3719 20.0118 11.9612 20.0112C7.55052 20.0107 3.97494 16.4235 3.97494 11.999C3.97494 7.57457 7.55052 3.98825 11.9612 3.98878Z"
          data-figma-gradient-fill="{&#34;type&#34;:&#34;GRADIENT_ANGULAR&#34;,&#34;stops&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.38823530077934265,&#34;g&#34;:0.14117647707462311,&#34;b&#34;:0.92156863212585449,&#34;a&#34;:1.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.94509804248809814,&#34;g&#34;:0.32156863808631897,&#34;b&#34;:0.61960786581039429,&#34;a&#34;:1.0},&#34;position&#34;:0.37999999523162842},{&#34;color&#34;:{&#34;r&#34;:1.0,&#34;g&#34;:1.0,&#34;b&#34;:1.0,&#34;a&#34;:0.0},&#34;position&#34;:1.0}],&#34;stopsVar&#34;:[{&#34;color&#34;:{&#34;r&#34;:0.38823530077934265,&#34;g&#34;:0.14117647707462311,&#34;b&#34;:0.92156863212585449,&#34;a&#34;:1.0},&#34;position&#34;:0.0},{&#34;color&#34;:{&#34;r&#34;:0.94509804248809814,&#34;g&#34;:0.32156863808631897,&#34;b&#34;:0.61960786581039429,&#34;a&#34;:1.0},&#34;position&#34;:0.37999999523162842},{&#34;color&#34;:{&#34;r&#34;:1.0,&#34;g&#34;:1.0,&#34;b&#34;:1.0,&#34;a&#34;:0.0},&#34;position&#34;:1.0}],&#34;transform&#34;:{&#34;m00&#34;:23.922433853149414,&#34;m01&#34;:-3.4911021962831176e-15,&#34;m02&#34;:3.4911021962831176e-15,&#34;m10&#34;:0.0028481325134634972,&#34;m11&#34;:-23.997152328491211,&#34;m12&#34;:23.997152328491211},&#34;opacity&#34;:1.0,&#34;blendMode&#34;:&#34;NORMAL&#34;,&#34;visible&#34;:true}"
        />
        <path
          d="M23.9953 11.9069C23.9979 10.798 23.1049 9.89908 22.0007 9.89908C20.8966 9.89908 19.9994 10.798 19.9968 11.9069C19.9943 13.0158 20.8873 13.9148 21.9914 13.9148C23.0956 13.9148 23.9927 13.0158 23.9953 11.9069Z"
          fill="#6324EB"
        />
        <defs>
          <clipPath id="story_loading_gradient_clip_path">
            <path
              d="M11.9612 23.9986C18.5672 23.9994 23.9224 18.6281 23.9224 12.0014C23.9224 5.37479 18.5672 0.00221056 11.9612 0.00142407C5.35522 0.000637576 0 5.37195 0 11.9986C0 18.6252 5.35522 23.9978 11.9612 23.9986ZM11.9612 3.98878C16.3719 3.9893 19.9475 7.57647 19.9475 12.001C19.9475 16.4254 16.3719 20.0118 11.9612 20.0112C7.55052 20.0107 3.97494 16.4235 3.97494 11.999C3.97494 7.57457 7.55052 3.98825 11.9612 3.98878Z"
            />
          </clipPath>
        </defs>
      </svg>
      <span class="generation-status-text">正在制作中...{{ progressText }}</span>
    </div>

    <div v-else-if="isFailed" class="failure-status" aria-live="polite" @click.stop>
      <span class="failure-status-text">创作服务暂时不可用</span>
      <div class="failure-actions">
        <button
          class="retry-button"
          type="button"
          :disabled="isRetrying"
          :aria-busy="isRetrying"
          @click="handleRetryClick"
        >
          <SvgIcon name="story-retry" :size="12" :loading="isRetrying" />
          <span>重试</span>
        </button>
        <button
          class="failure-delete-button"
          type="button"
          :disabled="isRetrying"
          @click="openDeleteConfirm"
        >
          <SvgIcon name="story-failure-delete" :size="14" />
          <span>删除</span>
        </button>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div v-else class="card-actions" @click.stop>
      <button
        class="action-btn"
        :class="{ 'is-active': story.isReading }"
        type="button"
        @click="$emit('read', story, true)"
      >
        <SvgIcon class="desktop-action-icon" name="play" :size="24" />
        <SvgIcon class="mobile-action-icon" name="story-read" :size="16" />
        <span>阅读</span>
      </button>
      <button class="action-btn" type="button" @click="handleEditClick">
        <SvgIcon class="desktop-action-icon" name="edit" :size="24" />
        <SvgIcon class="mobile-action-icon" name="story-edit" :size="16" />
        <span>编辑</span>
      </button>
      <button
        class="action-btn action-btn--share"
        type="button"
        :disabled="isSharing"
        :aria-busy="isSharing"
        @click="handleShareClick"
      >
        <SvgIcon class="desktop-action-icon" name="share" :size="24" :loading="isSharing" />
        <SvgIcon class="mobile-action-icon" name="story-share" :size="16" :loading="isSharing" />
        <span>分享</span>
      </button>
      <button class="action-btn action-btn--more" type="button" @click="openMoreSheet">
        <SvgIcon name="character-more" :size="16" />
        <span>更多</span>
      </button>
      <button class="action-btn" type="button" @click="$emit('exportPdf', story)">
        <SvgIcon name="editor-export-pdf" :size="24" />
        <span>导出PDF</span>
      </button>
      <button class="action-btn" type="button" @click="openDeleteConfirm">
        <SvgIcon name="trash" :size="24" />
        <span>删除</span>
      </button>
    </div>
  </div>

  <ConfirmDialog
    v-model="isDeleteConfirmVisible"
    message="您确定删除该故事绘本吗？"
    @confirm="handleDeleteConfirm"
  />

  <Teleport to="body">
    <Transition name="story-audit-sheet-fade">
      <div
        v-if="props.compact && isAuditRejected && isAuditPopoverVisible"
        class="story-audit-sheet-overlay"
        role="presentation"
        @click.self="closeAuditDialog"
        @keydown.esc="closeAuditDialog"
      >
        <section
          class="story-audit-sheet"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="auditDialogTitleId"
        >
          <header class="story-audit-sheet__header">
            <h2 :id="auditDialogTitleId" class="story-audit-sheet__title">审核未通过提示</h2>
            <button
              class="story-audit-sheet__close"
              type="button"
              aria-label="关闭审核未通过提示"
              @click="closeAuditDialog"
            >
              <SvgIcon name="modal-close" :size="12" />
            </button>
          </header>

          <div class="story-audit-sheet__body">
            <div>
              很抱歉，您的绘本审核未通过，原因是
              <span class="story-audit-sheet__opinion" v-html="auditOpinionHtml"></span>
            </div>
            <div class="audit-customer-service-row">
              <span>详情咨询客服：</span>
              <el-popover
                v-model:visible="isCustomerServiceVisible"
                placement="top-start"
                :width="188"
                :offset="8"
                :show-arrow="true"
                :persistent="false"
                trigger="click"
                popper-class="story-customer-service-popover"
              >
                <template #reference>
                  <button class="audit-customer-service__trigger" type="button" @click.stop>
                    点击添加
                  </button>
                </template>

                <section class="audit-customer-service" aria-label="微信扫码添加客服">
                  <header class="audit-customer-service__header">
                    <strong class="audit-customer-service__title">微信扫码添加客服</strong>
                    <button
                      class="audit-customer-service__close"
                      type="button"
                      aria-label="关闭客服二维码"
                      @click.stop="isCustomerServiceVisible = false"
                    >
                      <SvgIcon name="modal-close" :size="12" />
                    </button>
                  </header>
                  <img
                    class="audit-customer-service__qr"
                    :src="customerServiceQrUrl"
                    alt="添加客服二维码"
                  />
                  <span class="audit-customer-service__hours">工作日 9:00 – 18:00</span>
                </section>
              </el-popover>
            </div>
          </div>

          <footer class="story-audit-sheet__footer">
            <button
              class="story-audit-sheet__confirm"
              type="button"
              @click="closeAuditDialog"
            >
              我知道了
            </button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="story-title-dialog-fade">
      <div
        v-if="isTitleEditVisible"
        class="story-title-dialog-overlay"
        role="presentation"
        @click.self="closeTitleEditDialog"
        @keydown.esc="closeTitleEditDialog"
      >
        <section
          class="story-title-dialog"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleEditDialogTitleId"
        >
          <header class="story-title-dialog__header">
            <h2 :id="titleEditDialogTitleId" class="story-title-dialog__title">修改名称</h2>
            <button
              class="story-title-dialog__close"
              type="button"
              aria-label="关闭修改名称弹窗"
              :disabled="isUpdatingTitle"
              @click="closeTitleEditDialog"
            >
              <SvgIcon name="modal-close" :size="12" />
            </button>
          </header>

          <form class="story-title-dialog__form" @submit.prevent="handleTitleUpdate">
            <input
              ref="titleEditInputRef"
              v-model="titleDraft"
              class="story-title-dialog__input"
              type="text"
              aria-label="故事名称"
              autocomplete="off"
              :disabled="isUpdatingTitle"
            />

            <footer class="story-title-dialog__footer">
              <button
                class="story-title-dialog__button story-title-dialog__button--cancel"
                type="button"
                :disabled="isUpdatingTitle"
                @click="closeTitleEditDialog"
              >
                取消
              </button>
              <button
                class="story-title-dialog__button story-title-dialog__button--confirm"
                type="submit"
                :disabled="!canSubmitTitle"
                :aria-busy="isUpdatingTitle"
              >
                确定
              </button>
            </footer>
          </form>
        </section>
      </div>
    </Transition>
  </Teleport>

  <MobileEditTipSheet v-model="isMobileEditTipVisible" :edit-url="editShareUrl" />

  <Teleport to="body">
    <Transition name="story-more-fade">
      <div
        v-if="isMoreSheetVisible"
        class="story-more-overlay"
        role="presentation"
        @click="closeMoreSheet"
      >
        <section
          class="story-more-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="story-more-title"
          @click.stop
        >
          <header class="story-more-header">
            <h2 id="story-more-title" class="story-more-title">更多</h2>
            <button
              class="story-more-collapse"
              type="button"
              aria-label="关闭更多操作"
              @click="closeMoreSheet"
            >
              <SvgIcon name="mobile-edit-collapse" :size="12" />
            </button>
          </header>

          <div class="story-more-actions">
            <button
              v-for="item in moreSheetItems"
              :key="item.key"
              class="story-more-action"
              type="button"
              :disabled="item.key === 'share' && isSharing"
              :aria-busy="item.key === 'share' && isSharing"
              @click="handleMoreActionClick(item.key, $event)"
            >
              <SvgIcon :name="item.icon" :size="16" :loading="item.key === 'share' && isSharing" />
              <span>{{ item.label }}</span>
            </button>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onDeactivated,
    ref,
    useId,
    watch
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { ElMessage, ElMessageBox } from 'element-plus';
  import { updateStoryTitle } from '@/api/story';
  import { getStoryProgress, retryStory } from '@/api/storybook';
  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import MobileEditTipSheet from '@/components/common/MobileEditTipSheet.vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import customerServiceQrUrl from '@/assets/images/customer-service-qr.png';
  import type { StoryItem } from '@/types';

  const router = useRouter();

  const props = defineProps<{
    story: StoryItem;
    thumbnailTimestamp: number;
    isFreeUser: boolean;
    compact: boolean;
    /** 父组件正在为该故事执行分享准备时为 true */
    isSharing?: boolean;
  }>();

  const emit = defineEmits<{
    read: [story: StoryItem, fromReadButton?: boolean];
    edit: [story: StoryItem];
    share: [story: StoryItem, event: MouseEvent];
    retry: [story: StoryItem];
    exportPdf: [story: StoryItem];
    delete: [story: StoryItem];
    titleUpdated: [storyId: string, title: string];
    upgrade: [];
  }>();

  const isDeleteConfirmVisible = ref(false);
  const isTitleEditVisible = ref(false);
  const isMobileEditTipVisible = ref(false);
  const isMoreSheetVisible = ref(false);
  const isAuditPopoverVisible = ref(false);
  const isCustomerServiceVisible = ref(false);
  const isRetrying = ref(false);
  const isUpdatingTitle = ref(false);
  const titleDraft = ref('');
  const titleEditInputRef = ref<HTMLInputElement>();
  const titleEditDialogTitleId = `story-title-edit-dialog-${useId()}`;
  const auditDialogTitleId = `story-audit-dialog-${useId()}`;
  const taskUiState = ref<StoryUiState | null>(null);
  const taskProgress = ref<number | null>(null);
  const thumbnailRefreshVersion = ref(0);
  let taskPollingTimer: number | undefined;
  let taskPollingVersion = 0;
  let isDeactivated = false;

  type StoryUiState = 'ready' | 'generating' | 'failed';

  const TASK_POLL_INTERVAL_MS = 3000;

  const STATUS_UI_MAP: Record<number, StoryUiState> = {
    0: 'ready',
    1: 'ready',
    2: 'ready',
    [-5]: 'generating',
    [-6]: 'failed',
    [-7]: 'generating',
    [-8]: 'failed',
    [-9]: 'failed',
    [-11]: 'failed'
  };

  const AUDIT_HTML_ALLOWED_TAGS = new Set([
    'A',
    'B',
    'BLOCKQUOTE',
    'BR',
    'CODE',
    'DEL',
    'DIV',
    'EM',
    'I',
    'LI',
    'OL',
    'P',
    'PRE',
    'S',
    'SPAN',
    'STRONG',
    'U',
    'UL'
  ]);
  const AUDIT_HTML_DROP_CONTENT_TAGS = new Set([
    'EMBED',
    'IFRAME',
    'MATH',
    'OBJECT',
    'SCRIPT',
    'STYLE',
    'SVG',
    'TEMPLATE'
  ]);

  const storyUiState = computed(
    () => taskUiState.value ?? STATUS_UI_MAP[props.story.bStatus] ?? 'ready'
  );
  const isGenerating = computed(() => storyUiState.value === 'generating');
  const isFailed = computed(() => storyUiState.value === 'failed');
  const isAuditRejected = computed(() => props.story.auditStatus > 1);
  const isAuditPending = computed(
    () => !isAuditRejected.value && props.story.auditStatus === 0 && props.isFreeUser
  );
  const auditOpinionHtml = computed(() => sanitizeAuditOpinion(props.story.auditOpinion));
  const isInteractionBlocked = computed(() => storyUiState.value !== 'ready');
  const canSubmitTitle = computed(() => Boolean(titleDraft.value.trim()) && !isUpdatingTitle.value);

  const coverImageSrc = computed(() => {
    const source = props.story.coverUrl;
    if (!source) return source;

    const hashIndex = source.indexOf('#');
    const baseUrl = hashIndex === -1 ? source : source.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : source.slice(hashIndex);
    const separator = baseUrl.includes('?') ? '&' : '?';

    return `${baseUrl}${separator}t=${props.thumbnailTimestamp}-${thumbnailRefreshVersion.value}${hash}`;
  });

  const progressText = computed(() => {
    const progress = taskProgress.value ?? props.story.generateProgress;
    return typeof progress === 'number' && Number.isFinite(progress) ? `${progress}%` : '';
  });

  watch(
    () => [props.story.id, props.story.bStatus] as const,
    () => {
      if (!isDeactivated) startTaskPolling();
    },
    { immediate: true }
  );

  watch(isAuditPopoverVisible, (visible) => {
    if (!visible) isCustomerServiceVisible.value = false;
  });

  onActivated(() => {
    if (!isDeactivated) return;

    isDeactivated = false;
    startTaskPolling();
  });

  onDeactivated(() => {
    isDeactivated = true;
    stopTaskPolling();
  });

  onBeforeUnmount(stopTaskPolling);

  function startTaskPolling(force = false) {
    stopTaskPolling();
    taskUiState.value = null;
    taskProgress.value = null;

    if ((!force && ![-5, -7].includes(props.story.bStatus)) || !props.story.id) return;

    if (force) taskUiState.value = 'generating';

    const pollingVersion = taskPollingVersion;
    const storyId = props.story.id;

    const queryTaskProgress = async () => {
      try {
        const progress = await getStoryProgress(storyId);
        if (pollingVersion !== taskPollingVersion) return;

        if (Number.isFinite(progress.progress)) {
          taskProgress.value = Math.max(0, Math.min(100, progress.progress));
        }

        if (progress.bStatus === 1) {
          if (force) thumbnailRefreshVersion.value += 1;
          taskUiState.value = 'ready';
          return;
        }

        if ([-8, -9, -11].includes(progress.bStatus)) {
          taskUiState.value = 'failed';
          return;
        }
      } catch (error) {
        if (pollingVersion !== taskPollingVersion) return;
        console.warn('[story-list] 查询任务进度失败，将重试:', error);
      }

      taskPollingTimer = window.setTimeout(queryTaskProgress, TASK_POLL_INTERVAL_MS);
    };

    void queryTaskProgress();
  }

  function stopTaskPolling() {
    taskPollingVersion += 1;

    if (taskPollingTimer !== undefined) {
      window.clearTimeout(taskPollingTimer);
      taskPollingTimer = undefined;
    }
  }

  const moreSheetItems = [
    { key: 'read', label: '阅读', icon: 'story-read' },
    { key: 'edit', label: '编辑', icon: 'story-edit' },
    { key: 'share', label: '分享', icon: 'story-more-share' },
    { key: 'rename', label: '修改绘本名称', icon: 'story-more-rename' },
    { key: 'delete', label: '删除', icon: 'story-more-delete' }
  ] as const;

  const editShareUrl = computed(() => {
    const resolved = router.resolve({ name: 'edit', params: { id: props.story.id } });
    return `${window.location.origin}${resolved.href}`;
  });

  const handleCardClick = () => {
    if (isInteractionBlocked.value) return;

    emit('read', props.story);
  };

  const openTitleEditDialog = async () => {
    if (isInteractionBlocked.value) return;

    titleDraft.value = props.story.title;
    isTitleEditVisible.value = true;
    await nextTick();
    titleEditInputRef.value?.focus();
    titleEditInputRef.value?.select();
  };

  const closeTitleEditDialog = () => {
    if (isUpdatingTitle.value) return;

    isTitleEditVisible.value = false;
  };

  const handleTitleUpdate = async () => {
    const nextTitle = titleDraft.value.trim();
    if (!nextTitle || isUpdatingTitle.value) return;

    if (nextTitle === props.story.title) {
      closeTitleEditDialog();
      return;
    }

    isUpdatingTitle.value = true;

    try {
      await updateStoryTitle(props.story.id, nextTitle);
      emit('titleUpdated', props.story.id, nextTitle);
      isTitleEditVisible.value = false;
    } catch (error) {
      console.error('修改故事名称失败', error);
      void ElMessageBox.alert(getTitleUpdateErrorMessage(error), '修改失败', {
        confirmButtonText: '知道了',
        type: 'error'
      }).catch(() => undefined);
    } finally {
      isUpdatingTitle.value = false;
    }
  };

  const handleEditClick = () => {
    if (isInteractionBlocked.value) return;

    if (props.compact) {
      isMobileEditTipVisible.value = true;
      return;
    }

    emit('edit', props.story);
  };

  const openMoreSheet = () => {
    if (isInteractionBlocked.value) return;

    isMoreSheetVisible.value = true;
  };

  const closeMoreSheet = () => {
    isMoreSheetVisible.value = false;
  };

  const closeAuditDialog = () => {
    isCustomerServiceVisible.value = false;
    isAuditPopoverVisible.value = false;
  };

  const handleMoreActionClick = (
    key: (typeof moreSheetItems)[number]['key'],
    event: MouseEvent
  ) => {
    // 先关闭「更多」面板，再触发对应操作（分享时会转而打开分享面板）
    closeMoreSheet();

    switch (key) {
      case 'read':
        emit('read', props.story, false);
        break;
      case 'edit':
        isMobileEditTipVisible.value = true;
        break;
      case 'share':
        if (props.isSharing) return;
        // 「更多」面板收起有 200ms 滑出动画，等它退场后再打开分享面板，
        // 避免两个底部弹层同时滑动显得突兀；event 供 PC 端面板定位（移动端忽略）
        window.setTimeout(() => emit('share', props.story, event), 200);
        break;
      case 'rename':
        void openTitleEditDialog();
        break;
      case 'delete':
        openDeleteConfirm();
        break;
    }
  };
  /** 触发分享，并把点击位置传给父组件用于 PC 面板定位 */
  const handleShareClick = (event: MouseEvent) => {
    if (isInteractionBlocked.value || props.isSharing) return;

    emit('share', props.story, event);
  };

  const handleRetryClick = async () => {
    if (isRetrying.value || !props.story.id) return;

    const storyId = props.story.id;
    isRetrying.value = true;

    try {
      await retryStory(storyId);
      if (storyId !== props.story.id) return;

      startTaskPolling(true);
      emit('retry', props.story);
    } catch (error) {
      console.error('重试生成故事失败', error);
      ElMessage.error(
        error instanceof Error && error.message ? error.message : '重试失败，请稍后再试'
      );
    } finally {
      isRetrying.value = false;
    }
  };

  const openDeleteConfirm = () => {
    if (isGenerating.value) return;

    isDeleteConfirmVisible.value = true;
  };

  const handleDeleteConfirm = () => {
    emit('delete', props.story);
  };

  const pad = (n: number) => String(n).padStart(2, '0');

  const padDatePart = (value: string | undefined, fallback: number) => {
    const parsed = Number.parseInt(value ?? '', 10);
    return pad(Number.isNaN(parsed) ? fallback : parsed);
  };

  const normalizeTime = (timeStr?: string) => {
    const [hour, minute, second] = (timeStr || '').split(':');
    return `${padDatePart(hour, 0)}:${padDatePart(minute, 0)}:${padDatePart(second, 0)}`;
  };

  const formatDateOnly = (dateStr: string): string => {
    const trimmed = dateStr.trim();

    if (!trimmed) return '--';

    const [datePart] = trimmed.replace('T', ' ').split(/\s+/);
    const [year, month, day] = datePart.split(/[./-]/);

    if (!year || !month || !day) return trimmed;

    return `${year}-${padDatePart(month, 1)}-${padDatePart(day, 1)}`;
  };

  const formatDate = (dateStr: string): string => {
    const trimmed = dateStr.trim();

    if (!trimmed) return '--';

    const [datePart, timePart] = trimmed.replace('T', ' ').split(/\s+/);
    const [year, month, day] = datePart.split(/[./-]/);

    if (!year || !month || !day) return trimmed;

    return `${year}-${padDatePart(month, 1)}-${padDatePart(day, 1)} ${normalizeTime(timePart)}`;
  };

  const getTitleUpdateErrorMessage = (error: unknown) => {
    const fallback = '名称修改失败，请稍后重试';
    if (typeof error !== 'object' || error === null) return fallback;

    const errorRecord = error as {
      errorMsg?: unknown;
      msg?: unknown;
      message?: unknown;
      response?: { data?: { errorMsg?: unknown; msg?: unknown; message?: unknown } };
    };
    const responseData = errorRecord.response?.data;
    const candidates = [
      errorRecord.errorMsg,
      errorRecord.msg,
      responseData?.errorMsg,
      responseData?.msg,
      responseData?.message,
      errorRecord.message
    ];

    return (
      candidates.find((message): message is string => typeof message === 'string' && !!message) ??
      fallback
    );
  };

  const sanitizeAuditOpinion = (html: string) => {
    const source = html.trim();
    if (!source) return '未提供具体原因';

    const document = new DOMParser().parseFromString(source, 'text/html');

    for (const element of Array.from(document.body.querySelectorAll('*'))) {
      if (AUDIT_HTML_DROP_CONTENT_TAGS.has(element.tagName)) {
        element.remove();
        continue;
      }

      if (!AUDIT_HTML_ALLOWED_TAGS.has(element.tagName)) {
        element.replaceWith(...Array.from(element.childNodes));
        continue;
      }

      const href = element.tagName === 'A' ? element.getAttribute('href')?.trim() : undefined;
      const title = element.tagName === 'A' ? element.getAttribute('title') : null;
      const target = element.tagName === 'A' ? element.getAttribute('target') : null;
      for (const attribute of Array.from(element.attributes))
        element.removeAttribute(attribute.name);

      if (element.tagName !== 'A' || !href) continue;

      try {
        const url = new URL(href, window.location.origin);
        if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) continue;

        element.setAttribute('href', href);
        if (title) element.setAttribute('title', title);
        if (target === '_blank') {
          element.setAttribute('target', '_blank');
          element.setAttribute('rel', 'noopener noreferrer');
        }
      } catch {
        // Invalid links are rendered as plain anchor text without navigation.
      }
    }

    return document.body.innerHTML.trim() || '未提供具体原因';
  };
</script>

<style scoped>
  /* ===== 故事卡片 ===== */
  .story-card {
    display: flex;
    align-items: center;
    width: 100%;
    height: 130px;
    padding: 0 51px 0 21px;
    box-sizing: border-box;
    background: #fff;
    border: 1.25px solid #f3f4f6;
    border-radius: 16px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.2s;
  }

  .story-card:hover {
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.08);
  }

  /* 封面 */
  .card-cover {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 90px;
    height: 90px;
    padding: 0;
    flex-shrink: 0;
    border: 0;
    border-radius: 4px;
    background: #f9f9f9;
    overflow: hidden;
    cursor: pointer;
  }

  .card-cover:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .card-cover:disabled {
    cursor: default;
  }

  .card-cover.is-generating-cover {
    background: transparent;
  }

  .cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .generating-cover-placeholder {
    width: 80px;
    height: 80px;
  }

  /* 信息区 */
  .card-info {
    flex: 1;
    min-width: 0;
    margin-left: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;
  }

  .card-title {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 6px;
    width: 100%;
    max-width: 100%;
    margin: 0;
    color: #333;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  .card-title-button {
    display: block;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    padding: 0;
    overflow: hidden;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    line-height: inherit;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .audit-rejection-badge,
  .audit-pending-badge {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 2px 4px;
    flex: 0 0 auto;
    box-sizing: border-box;
    border: 0;
    border-radius: 2px;
    background: #ffe7e7;
    font-family: 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    white-space: nowrap;
    cursor: pointer;
  }

  .audit-pending-badge {
    color: #e25555;
    cursor: default;
  }

  .audit-pending-badge__upgrade {
    padding: 0;
    border: 0;
    background: transparent;
    color: #6033E1;
    font: inherit;
    line-height: inherit;
    cursor: pointer;
  }

  .audit-rejection-badge__message {
    color: #e25555;
  }

  .audit-rejection-badge__action {
    color: #6033e1;
  }

  .audit-rejection-badge:focus-visible,
  .audit-pending-badge__upgrade:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  :global(.story-audit-rejection-popover.el-popper) {
    max-width: calc(100vw - 32px);
    padding: 24px;
    box-sizing: border-box;
    border: 0;
    border-radius: 12px;
    box-shadow: 0 3px 40px rgba(0, 0, 0, 0.15);
  }

  :global(.story-customer-service-popover.el-popper) {
    z-index: 4200 !important;
    width: 188px !important;
    min-width: 188px;
    padding: 12px;
    box-sizing: border-box;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.14);
  }

  :global(.story-customer-service-popover.el-popper .el-popper__arrow::before) {
    border-color: #f0f0f0;
    background: #fff;
  }

  .audit-rejection-dialog {
    display: flex;
    flex-direction: column;
    gap: 16px;
    color: #333940;
    font-weight: 400;
  }

  .audit-rejection-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 24px;
  }

  .audit-rejection-dialog__title {
    margin: 0;
    font-family: 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  .audit-rejection-dialog__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #999;
    cursor: pointer;
  }

  .audit-rejection-dialog__body {
    margin: 0;
    overflow-wrap: anywhere;
    font-family: 'Inter', 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    line-height: 22px;
  }

  .audit-rejection-dialog__opinion :deep(p) {
    margin: 0;
  }

  .audit-rejection-dialog__opinion {
    color: #e25555;
  }

  .audit-customer-service-row {
    display: flex;
    align-items: baseline;
    min-height: 22px;
    color: #333940;
    white-space: nowrap;
  }

  .audit-customer-service__trigger {
    padding: 0;
    border: 0;
    background: transparent;
    color: #1677ff;
    font: inherit;
    line-height: inherit;
    cursor: pointer;
  }

  .audit-customer-service {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #333940;
  }

  .audit-customer-service__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 154px;
    height: 16px;
  }

  .audit-customer-service__title {
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
  }

  .audit-customer-service__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    flex: 0 0 16px;
    border: 0;
    background: transparent;
    color: #999;
    cursor: pointer;
  }

  .audit-customer-service__qr {
    display: block;
    width: 138px;
    height: 138px;
  }

  .audit-customer-service__hours {
    color: #9ca3af;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 11px;
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
  }

  .audit-customer-service__trigger:focus-visible,
  .audit-customer-service__close:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .audit-rejection-dialog__footer {
    display: flex;
    justify-content: flex-end;
  }

  .audit-rejection-dialog__confirm {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 40px;
    padding: 0 12px;
    box-sizing: border-box;
    border: 0;
    border-radius: 6px;
    background: #6033e1;
    color: #fff;
    font-family: 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    cursor: pointer;
  }

  .audit-rejection-dialog__close:hover,
  .audit-rejection-dialog__close:focus-visible {
    background: #f5f3fd;
  }

  .audit-rejection-dialog__confirm:hover,
  .audit-rejection-dialog__confirm:focus-visible {
    background: #5128c7;
  }

  .audit-rejection-dialog__close:focus-visible,
  .audit-rejection-dialog__confirm:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-audit-sheet-overlay {
    position: fixed;
    inset: 0;
    z-index: 4100;
    display: flex;
    align-items: flex-end;
    background: rgba(0, 0, 0, 0.3);
  }

  .story-audit-sheet {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 204px;
    max-height: calc(100vh - 16px);
    max-height: calc(100dvh - 16px);
    padding: 16px;
    box-sizing: border-box;
    border-radius: 16px 16px 0 0;
    background: #fff;
    color: #333940;
  }

  .story-audit-sheet__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 24px;
    flex: 0 0 24px;
  }

  .story-audit-sheet__title {
    margin: 0;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
  }

  .story-audit-sheet__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    flex: 0 0 24px;
    border: 0;
    background: transparent;
    color: #222;
    cursor: pointer;
  }

  .story-audit-sheet__body {
    min-height: 66px;
    margin-top: 16px;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-wrap: anywhere;
    overscroll-behavior: contain;
    font-family: 'Inter', 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  .story-audit-sheet__opinion,
  .story-audit-sheet__opinion :deep(*) {
    color: #ffb000;
  }

  .story-audit-sheet__opinion :deep(p) {
    margin: 0;
  }

  .story-audit-sheet__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    margin-bottom: 10px;
    flex: 0 0 40px;
  }

  .story-audit-sheet__confirm {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 40px;
    padding: 0 12px;
    box-sizing: border-box;
    border: 0;
    border-radius: 6px;
    background: #6033e1;
    color: #fff;
    font-family: 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    cursor: pointer;
  }

  .story-audit-sheet__close:focus-visible,
  .story-audit-sheet__confirm:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-audit-sheet-fade-enter-active,
  .story-audit-sheet-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .story-audit-sheet-fade-enter-active .story-audit-sheet,
  .story-audit-sheet-fade-leave-active .story-audit-sheet {
    transition: transform 0.2s ease;
  }

  .story-audit-sheet-fade-enter-from,
  .story-audit-sheet-fade-leave-to {
    opacity: 0;
  }

  .story-audit-sheet-fade-enter-from .story-audit-sheet,
  .story-audit-sheet-fade-leave-to .story-audit-sheet {
    transform: translateY(100%);
  }

  .card-title-button:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .card-title-button:disabled {
    cursor: default;
  }

  .card-title-edit-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    flex: 0 0 16px;
    border: 0;
    background: transparent;
    color: #160211;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .story-card:not(.is-blocked) .card-title:hover .card-title-edit-button,
  .story-card:not(.is-blocked) .card-title:focus-within .card-title-edit-button {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  .card-title-edit-button:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  /* 日期元信息 */
  .card-meta {
    display: flex;
    align-items: center;
    gap: 31px;
    margin-left: 4px;
  }

  .story-card:not(.is-compact) {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr) auto;
    grid-template-rows: 24px 40px;
    align-content: center;
    align-items: center;
  }

  .story-card:not(.is-compact) .card-cover {
    grid-column: 1;
    grid-row: 1 / 3;
  }

  .story-card:not(.is-compact) .card-info {
    display: contents;
  }

  .story-card:not(.is-compact) .card-title {
    grid-column: 2 / 4;
    grid-row: 1;
    width: auto;
    margin-left: 16px;
  }

  .story-card:not(.is-compact) .card-meta {
    grid-column: 2;
    grid-row: 2;
    align-self: end;
    margin-left: 20px;
  }

  .story-card:not(.is-compact) .card-actions,
  .story-card:not(.is-compact) .generation-status,
  .story-card:not(.is-compact) .failure-status {
    grid-column: 3;
    grid-row: 2;
  }

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #9ca3af;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    white-space: nowrap;
  }

  /* 操作按钮组 */
  .card-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: 24px;
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 40px;
    padding: 0 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #333;
    font-family: 'Inter', 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
    cursor: pointer;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .action-btn:hover,
  .action-btn.is-active {
    background: #ede8ff;
    color: #6033e1;
  }

  .action-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .action-btn:disabled:hover {
    background: transparent;
    color: #333;
  }

  .action-btn:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-card.is-blocked {
    cursor: default;
  }

  .story-card.is-blocked:hover {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.04);
  }

  .story-title-dialog-overlay {
    position: fixed;
    inset: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.5);
  }

  .story-title-dialog {
    width: 400px;
    max-width: 100%;
    min-height: 234px;
    padding: 23px 18px;
    box-sizing: border-box;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  }

  .story-title-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 24px;
  }

  .story-title-dialog__title {
    margin: 0;
    color: #333940;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }

  .story-title-dialog__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #999;
    cursor: pointer;
  }

  .story-title-dialog__close:hover:not(:disabled) {
    background: #f5f3fd;
    color: #6033e1;
  }

  .story-title-dialog__form {
    display: flex;
    flex-direction: column;
    margin-top: 34px;
  }

  .story-title-dialog__input {
    width: 100%;
    height: 44px;
    padding: 0 10px;
    box-sizing: border-box;
    border: 1px solid #d9dce1;
    border-radius: 6px;
    background: #fff;
    color: #160211;
    font-family: 'Inter', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    outline: none;
  }

  .story-title-dialog__input:focus {
    border-color: #6033e1;
  }

  .story-title-dialog__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 38px;
  }

  .story-title-dialog__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 44px;
    padding: 0;
    box-sizing: border-box;
    border-radius: 8px;
    font-family: 'Arimo', 'Inter', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    cursor: pointer;
  }

  .story-title-dialog__button--cancel {
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    color: #160211;
  }

  .story-title-dialog__button--confirm {
    border: 0;
    background: #6033e1;
    color: #fff;
  }

  .story-title-dialog__button--cancel:hover:not(:disabled) {
    background: #f9fafb;
  }

  .story-title-dialog__button--confirm:hover:not(:disabled) {
    background: #5128c7;
  }

  .story-title-dialog__button:disabled,
  .story-title-dialog__close:disabled,
  .story-title-dialog__input:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .story-title-dialog__button:focus-visible,
  .story-title-dialog__close:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-title-dialog-fade-enter-active,
  .story-title-dialog-fade-leave-active {
    transition: opacity 0.15s ease;
  }

  .story-title-dialog-fade-enter-from,
  .story-title-dialog-fade-leave-to {
    opacity: 0;
  }

  .generation-status {
    display: inline-flex;
    align-items: center;
    gap: 21px;
    flex: 0 0 auto;
    margin-left: 24px;
    margin-right: 21px;
    color: #6e6a7c;
  }

  .generation-loading-icon {
    display: block;
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
    animation: generation-spin 0.9s linear infinite;
  }

  .generation-status-text {
    color: #6e6a7c;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 14px;
    white-space: nowrap;
  }

  .failure-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    margin-left: 24px;
  }

  .failure-status-text {
    color: #999;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 26px;
    white-space: nowrap;
  }

  .failure-actions {
    display: inline-flex;
    align-items: center;
    width: 168px;
    height: 40px;
    padding: 0 11px 0 12px;
    box-sizing: border-box;
    gap: 10px;
  }

  .retry-button,
  .failure-delete-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 6px 12px;
    box-sizing: border-box;
    border: 1px solid #6e6a7c;
    border-radius: 8px;
    background: transparent;
    color: #6e6a7c;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    white-space: nowrap;
    cursor: pointer;
  }

  .retry-button {
    width: 68px;
    gap: 6px;
  }

  .failure-delete-button {
    width: 67px;
    gap: 3px;
    line-height: 22px;
  }

  .retry-button:hover:not(:disabled),
  .failure-delete-button:hover:not(:disabled) {
    border-color: #6033e1;
    color: #6033e1;
  }

  .retry-button:disabled,
  .failure-delete-button:disabled {
    cursor: wait;
  }

  .retry-button:focus-visible,
  .failure-delete-button:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .mobile-action-icon,
  .mobile-date,
  .action-btn--more {
    display: none;
  }

  .story-more-overlay {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: flex-end;
    background: rgba(0, 0, 0, 0.3);
  }

  .story-more-sheet {
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 16px 16px 0 0;
    background: #fff;
  }

  .story-more-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 24px;
  }

  .story-more-title {
    margin: 0;
    color: #160211;
    font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
  }

  .story-more-collapse {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 24px;
    padding: 0 16px;
    box-sizing: border-box;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #fff;
    color: #160211;
    cursor: pointer;
  }

  .story-more-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .story-more-action {
    display: inline-flex;
    align-items: center;
    width: 100%;
    height: 42px;
    padding: 10px;
    box-sizing: border-box;
    gap: 8px;
    border: 0;
    background: transparent;
    color: #160211;
    font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 16px;
    cursor: pointer;
    text-align: left;
  }

  .story-more-action:active,
  .story-more-collapse:active {
    background: #f9fafb;
  }

  .story-more-action:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .story-more-action:focus-visible,
  .story-more-collapse:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .story-more-fade-enter-active,
  .story-more-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .story-more-fade-enter-active .story-more-sheet,
  .story-more-fade-leave-active .story-more-sheet {
    transition: transform 0.2s ease;
  }

  .story-more-fade-enter-from,
  .story-more-fade-leave-to {
    opacity: 0;
  }

  .story-more-fade-enter-from .story-more-sheet,
  .story-more-fade-leave-to .story-more-sheet {
    transform: translateY(100%);
  }

  @keyframes generation-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ===== 移动端适配 ===== */
  .story-card.is-compact {
    height: 60px;
    padding: 0;
    gap: 0;
    align-items: center;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .story-card.is-compact:hover {
    box-shadow: none;
  }

  .story-card.is-compact.has-audit-notice {
    position: relative;
    height: 82px;
    align-items: flex-start;
  }

  .story-card.is-compact.has-audit-pending {
    height: auto;
    min-height: 82px;
    flex-wrap: wrap;
  }

  .story-card.is-compact .card-cover {
    width: 107px;
    height: 60px;
    min-width: 88px;
    flex: 0 1 107px;
    border-radius: 8px;
    background: transparent;
  }

  .story-card.is-compact .generating-cover-placeholder {
    width: 60px;
    height: 60px;
  }

  .story-card.is-compact .card-info {
    width: auto;
    flex: 1 1 96px;
    min-width: 0;
    margin-left: clamp(8px, 3vw, 12px);
    align-items: flex-start;
    gap: 8px;
  }

  .story-card.is-compact .card-title {
    width: 100%;
    color: #160211;
    font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
  }

  .story-card.is-compact .card-title.has-audit-rejection .card-title-button,
  .story-card.is-compact .card-title.has-audit-pending .card-title-button {
    max-width: 100%;
  }

  .story-card.is-compact .audit-rejection-badge,
  .story-card.is-compact .audit-pending-badge {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    max-width: none;
    height: 22px;
    padding: 2px 4px;
    overflow: hidden;
    justify-content: flex-start;
    font-size: 12px;
    line-height: 18px;
    text-align: left;
    text-overflow: ellipsis;
  }

  .story-card.is-compact .audit-pending-badge {
    width: calc(100% - 4px);
    color: #ff5556;
  }

  .story-card.is-compact .audit-pending-badge--compact {
    display: block;
    position: static;
    order: 1;
    width: 100%;
    height: auto;
    min-height: 22px;
    flex: 0 0 100%;
    overflow: visible;
    overflow-wrap: anywhere;
    white-space: normal;
    text-overflow: clip;
  }

  .story-card.is-compact.has-audit-notice .card-info {
    height: 60px;
  }

  .story-card.is-compact .audit-rejection-badge__message {
    overflow: hidden;
    color: #ff5556;
    text-overflow: ellipsis;
  }

  .story-card.is-compact .audit-rejection-badge__action {
    margin-left: 4px;
  }

  .story-card.is-compact.has-audit-notice .card-actions,
  .story-card.is-compact.has-audit-notice .generation-status,
  .story-card.is-compact.has-audit-notice .failure-status {
    margin-top: 12px;
  }

  .story-card.is-compact .card-title-button {
    max-width: 100%;
  }

  .story-card.is-compact .card-title-edit-button {
    display: none;
  }

  .story-card.is-compact .card-meta {
    max-width: 100%;
    margin-left: 0;
    gap: 0;
    overflow: hidden;
  }

  .story-card.is-compact .meta-item {
    display: none;
    gap: 0;
    color: #6e6a7c;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 350;
    line-height: 16px;
  }

  .story-card.is-compact .meta-item:first-child {
    display: inline-flex;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .story-card.is-compact .meta-item:first-child :deep(.svg-icon),
  .story-card.is-compact .desktop-date {
    display: none;
  }

  .story-card.is-compact .mobile-date {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .story-card.is-compact .card-actions {
    width: 124px;
    flex: 0 0 124px;
    margin-left: clamp(8px, 4vw, 21px);
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 8px;
  }

  .story-card.is-compact .generation-status {
    width: 124px;
    flex: 0 0 124px;
    margin-left: clamp(8px, 4vw, 21px);
    margin-right: 0;
    gap: 8px;
  }

  .story-card.is-compact .generation-loading-icon {
    width: 16px;
    height: 16px;
  }

  .story-card.is-compact .generation-status-text {
    max-width: 100px;
    overflow: hidden;
    color: #6e6a7c;
    font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 12px;
    line-height: 16px;
    text-overflow: ellipsis;
  }

  .story-card.is-compact .failure-status {
    width: 124px;
    height: 60px;
    flex: 0 0 124px;
    margin-left: clamp(8px, 4vw, 21px);
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 4px;
  }

  .story-card.is-compact .failure-status-text {
    max-width: 124px;
    overflow: hidden;
    font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 12px;
    line-height: 16px;
    text-overflow: ellipsis;
  }

  .story-card.is-compact .retry-button {
    width: 60px;
    height: 28px;
    padding: 4px 10px;
  }

  .story-card.is-compact .failure-actions {
    width: 124px;
    height: 28px;
    padding: 0;
    gap: 4px;
  }

  .story-card.is-compact .failure-delete-button {
    width: 60px;
    height: 28px;
    padding: 4px 8px;
  }

  .story-card.is-compact .action-btn {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    gap: 0;
    border-radius: 16px;
    background: transparent;
    color: #160211;
    font-size: 0;
  }

  .story-card.is-compact .action-btn span {
    display: none;
  }

  .story-card.is-compact .desktop-action-icon {
    display: none;
  }

  .story-card.is-compact .mobile-action-icon {
    display: inline-block;
  }

  .story-card.is-compact .action-btn :deep(.svg-icon) {
    width: 16px;
    height: 16px;
  }

  .story-card.is-compact .action-btn--share,
  .story-card.is-compact .action-btn:nth-child(n + 5) {
    display: none;
  }

  .story-card.is-compact .action-btn--more {
    display: inline-flex;
  }

  .story-card.is-compact .action-btn:hover,
  .story-card.is-compact .action-btn.is-active {
    background: transparent;
    color: #6033e1;
  }

  @media (max-width: 767px) {
    .story-title-dialog-overlay {
      padding: 16px;
    }

    .story-title-dialog {
      width: 100%;
    }
  }
</style>
